import { type VariantProps, cva } from "class-variance-authority";
import DOMPurify from "dompurify";

import { Show, createMemo, createSignal, splitProps } from "solid-js";
import type { JSX } from "solid-js/jsx-runtime";

import Icon from "@/components/Icon.tsx";
import { inputSize } from "@/hooks/usePublic.ts";
import { t } from "@/i18n";
import { isMobile } from "@/lib/isMobile.ts";
import { parseRegexString } from "@/lib/validateUtils.ts";

import { cn } from "../../../lib/tailwindcssTool.ts";
import "./Input.css";

export const inputVariants = cva("", {
  variants: {
    size: {
      default: "base-input-default-size",
      sm: "base-input-sm",
      lg: "base-input-lg",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

type BaseInputProps = {
  id?: string;
  name?: string;
  value?: string | number;
  placeholder?: string;
  IsFloatPlaceholder?: boolean;
  disabled?: boolean;
  required?: boolean;
  class?: string;
  error?: string;
  tips?: string;
  prefix?: JSX.Element;
  suffix?: JSX.Element;
  success?: boolean;
  maxLength?: number;
  showClose?: boolean;
  noAutoFill?: boolean;
  noDefaultBorder?: boolean;
  lock?: boolean;
  onChange?: JSX.ChangeEventHandlerUnion<HTMLInputElement, Event>;
  onInput?: JSX.ChangeEventHandlerUnion<HTMLInputElement, Event>;
  onBlur?: JSX.FocusEventHandlerUnion<HTMLInputElement, FocusEvent>;
  onFocus?: JSX.FocusEventHandlerUnion<HTMLInputElement, FocusEvent>;
  onKeyDown?: (e: KeyboardEvent) => void;
  onClose?: () => void;
  valueRegExp?: string;
  disableFloatLabel?: boolean;
} & VariantProps<typeof inputVariants>;

export type InputProps =
  | (BaseInputProps & {
      type?: "text" | "email" | "number" | "tel";
    })
  | (BaseInputProps & {
      type: "password";
      showPasswordToggle?: boolean;
      onEyeClick?: () => void;
    });

export function Input(props: InputProps) {
  const [local, variants, others] = splitProps(
    props,
    [
      "class",
      "type",
      "error",
      "value",
      "success",
      "prefix",
      "suffix",
      "maxLength",
      "showClose",
      "onClose",
      "noAutoFill",
      "valueRegExp",
      "tips",
      "noDefaultBorder",
      "IsFloatPlaceholder",
      "disabled",
      "lock",
    ],
    ["size"],
  );

  const [focused, setFocused] = createSignal(false);
  const [showPassword, setShowPassword] = createSignal(false);
  const [showTips, setShowTips] = createSignal(false);
  const [internalValue, setInternalValue] = createSignal(local.value || "");
  const sanitizedValue = createMemo(() =>
    DOMPurify.sanitize((local.value as string) || ""),
  );

  const hasValue = createMemo(() => {
    return Boolean(sanitizedValue() || internalValue());
  });

  const isPassword = local.type === "password";
  const passwordProps = props as Extract<InputProps, { type: "password" }>;
  const inputAutoFilling = {
    autocapitalize: "off" as const,
    autocorrect: "off" as const,
    autocomplete: "off" as const,
  };

  const inputPlaceholder = createMemo(() => {
    return focused() || hasValue() ? "" : props.placeholder;
  });

  const shouldFloatLabel = createMemo(() => {
    if (props.disableFloatLabel) return false;
    return (
      (focused() || hasValue()) &&
      props.placeholder &&
      (local.IsFloatPlaceholder ?? true)
    );
  });

  return (
    <div class="base-input-wrapper">
      <div
        class={cn(
          "placeholder:text-general-darkzi bg-general-enterbg gap-[5px] pr-[12px] placeholder:font-medium",
          isMobile() ? "h-[44px] text-[12px]" : "h-[48px] text-[14px]",
          "base-input-container",
          "base-input-base",
          focused()
            ? "base-input-focused" // 聚焦时只加 focus
            : local.error
              ? "base-input-error" // 失焦且有 error 时加 error
              : "",
          local.class,
          props.placeholder ? "pl-[12px]" : "pl-[12px]",
        )}
      >
        <Show when={local.prefix} fallback={<></>}>
          <div class="base-input-prefix">{local.prefix}</div>
        </Show>
        <div class="relative flex-1">
          <Show when={props.placeholder} fallback={<></>}>
            <div
              class={cn(
                "base-input-floating-label relative font-medium",
                isMobile() ? "text-[12px]" : "text-[14px]",
                shouldFloatLabel()
                  ? "base-input-floating-label-active"
                  : "base-input-floating-label-inactive",
                focused()
                  ? "text-general-main"
                  : local.error
                    ? "text-general-warning"
                    : "text-general-darkzi",
              )}
            >
              <Show when={focused() || local.error} fallback={<></>}>
                <div class="bg-general-enterbg absolute bottom-0 left-0 top-0 my-auto h-[3px] w-full"></div>
              </Show>
              <span class="relative">{props.placeholder}</span>
            </div>
          </Show>
          <input
            {...others}
            disabled={local.disabled || local.lock}
            type={
              isPassword
                ? showPassword()
                  ? "text"
                  : "password"
                : local.type || "text"
            }
            value={sanitizedValue()}
            class={cn(
              "no-spinner m-0 h-full w-full flex-1 border-0 p-0 font-[500] shadow-none outline-none focus:border-0 focus:shadow-none focus:outline-none",
              hasValue() ? "text-general-light" : "text-general-darkzi",
              shouldFloatLabel()
                ? "placeholder:opacity-0"
                : local.error
                  ? "placeholder:text-inherit"
                  : "placeholder:text-general-darkzi",
              isMobile()
                ? "text-[12px] placeholder:text-[12px]"
                : "text-[14px] placeholder:text-[14px]",
            )}
            placeholder={inputPlaceholder()}
            {...(local.noAutoFill ? inputAutoFilling : {})}
            autocomplete="new-password"
            onFocus={(e) => {
              setFocused(true);
              if (typeof others.onFocus === "function") {
                others.onFocus(e);
              }
            }}
            onBlur={(e) => {
              setFocused(false);
              if (typeof others.onBlur === "function") {
                others.onBlur(e);
              }
            }}
            onInput={(e) => {
              setInternalValue(e.target.value);
              if (typeof others.onInput === "function") {
                if (local.valueRegExp) {
                  const regex = parseRegexString(local.valueRegExp);
                  if (regex) {
                    const value = e.target.value.replace(regex, "");
                    e.target.value = value;
                    e.target.innerHTML = value;
                  }
                }
                others.onInput(e);
              }
            }}
            onKeyDown={(e) => {
              if (typeof others.onKeyDown === "function") {
                others.onKeyDown(e);
              }
            }}
            maxLength={local.maxLength}
          />
        </div>
        <Show
          when={isPassword && passwordProps.showPasswordToggle !== false}
          fallback={<></>}
        >
          <button
            type="button"
            class="base-input-password-toggle h-full"
            onClick={() => {
              setShowPassword(!showPassword());
              if (typeof passwordProps.onEyeClick === "function") {
                passwordProps.onEyeClick();
              }
            }}
            tabIndex={-1}
          >
            <Show
              when={showPassword()}
              fallback={<Icon name="general/eye" class="size-[16px]" />}
            >
              <Icon name="general/eye-1" class="size-[16px]" />
            </Show>
          </button>
        </Show>
        <Show when={local.showClose && hasValue()} fallback={<></>}>
          <Icon
            name="general/xx2"
            class="text-general-bright size-[9px]"
            wrapperClass={`base-input-close-btn ${inputSize(variants.size ?? "default").right} ${inputSize(variants.size ?? "default").top}`}
            onClick={local.onClose}
          />
        </Show>
        <Show when={local.lock} fallback={<></>}>
          <Icon
            name="general/lock"
            class={`text-general-icon ${isMobile() ? "size-[14px]" : "size-[16px]"}`}
          />
        </Show>
        <Show when={local.suffix && !isPassword} fallback={<></>}>
          <div class="base-input-suffix">{local.suffix}</div>
        </Show>
        <Show when={local.tips} fallback={<></>}>
          <div class="relative leading-[1]">
            <div
              class="relative"
              onMouseEnter={() => !isMobile() && setShowTips(true)}
              onMouseLeave={() => setShowTips(false)}
              onclick={() => setShowTips(!showTips())}
            >
              <Show when={showTips()} fallback={<></>}>
                <div class="absolute right-[-8px] top-[-3px] z-[2] flex w-[max-content] max-w-[350px] translate-y-[-100%] flex-col items-end">
                  <div class="bg-general-bg7 text-general-light rounded-[5px] p-[10px] text-[11px] font-medium leading-[1.2]">
                    {local.tips}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="10"
                    viewBox="0 0 12 10"
                    fill="none"
                    class="mr-[10px] shrink-0"
                  >
                    <path
                      d="M12 0H0L6.3923 10L12 0Z"
                      class="fill-general-bg7"
                    />
                  </svg>
                </div>
              </Show>
              <Icon
                name="general/gth"
                class="size-[16px]"
                color={showTips() ? "#FFFFFF" : "#B3B8C1"}
              />
            </div>
          </div>
        </Show>
      </div>

      <Show when={local.error && !focused()} fallback={<></>}>
        <p
          class={`base-input-error-message font-medium ${isMobile() ? "text-[12px]" : "text-[14px]"}`}
        >
          {local.error}
        </p>
      </Show>

      <Show when={isPassword && focused()} fallback={<></>}>
        <p
          class={`base-input-password-tip-message font-medium ${isMobile() ? "text-[12px]" : "text-[14px]"}`}
        >
          {t("auth.characters", {
            val: "6-30",
          })}
        </p>
      </Show>
    </div>
  );
}
